export const objectOrientedProgrammingCourse = {
  id: 'object-oriented-programming',
  title: 'Object-Oriented Programming',
  description: 'A comprehensive course covering object-oriented programming principles, design patterns, and advanced programming concepts using modern programming languages.',
  credits: 4,
  duration: '15 weeks',
  level: 'Intermediate',
  category: 'Coding',
  learningObjectives: [
    'Master fundamental OOP concepts: encapsulation, inheritance, and polymorphism',
    'Design and implement classes, objects, and their relationships',
    'Apply design patterns to solve common programming problems',
    'Understand abstraction and interface design principles',
    'Develop skills in code reusability and maintainability',
    'Learn advanced OOP concepts including composition and aggregation',
    'Implement exception handling and error management strategies',
    'Build complex applications using object-oriented design principles'
  ],
  prerequisites: ['Introduction to Programming', 'Basic data structures knowledge'],
  skills: [
    'Object-Oriented Design',
    'Class and Object Implementation',
    'Inheritance and Polymorphism',
    'Design Patterns',
    'UML Modeling',
    'Code Architecture',
    'Exception Handling',
    'Testing Object-Oriented Code',
    'Refactoring Techniques',
    'Interface Design',
    'Abstraction Principles',
    'Software Engineering Best Practices'
  ],
  marketDemand: 'Very High',
  averageSalary: '$75,000 - $140,000',
  careerPaths: [
    'Senior Software Developer',
    'Software Architect',
    'Systems Analyst',
    'Application Developer',
    'Enterprise Software Developer',
    'Game Developer',
    'Mobile Application Developer',
    'Backend Developer',
    'Full-Stack Developer',
    'Software Engineering Manager',
    'Technical Lead',
    'Solution Architect'
  ],
  industryApplications: [
    'Enterprise Software Development',
    'Game Development',
    'Mobile App Development',
    'Web Application Development',
    'Desktop Application Development',
    'Embedded Systems',
    'Financial Software',
    'Healthcare Management Systems',
    'E-commerce Platforms',
    'Content Management Systems',
    'Educational Software',
    'Manufacturing Systems'
  ],
  modules: [
    {
      id: 'oop-fundamentals',
      title: 'OOP Fundamentals',
      duration: '2 weeks',
      learningObjectives: [
        'Understand the core principles of object-oriented programming',
        'Learn the differences between procedural and object-oriented programming',
        'Master class and object concepts',
        'Implement basic encapsulation'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Introduction to Object-Oriented Programming',
          content: `# Introduction to Object-Oriented Programming

## What is Object-Oriented Programming?

Object-Oriented Programming (OOP) is a programming paradigm that organizes code around objects rather than functions and logic. Objects are instances of classes that contain both data (attributes) and methods (functions) that operate on that data.

## Core Principles of OOP

### 1. Encapsulation
Encapsulation is the practice of bundling data and methods that operate on that data within a single unit (class), and restricting direct access to some of the object's components.

### 2. Inheritance
Inheritance allows a class to inherit properties and methods from another class, promoting code reuse and creating hierarchical relationships.

### 3. Polymorphism
Polymorphism allows objects of different types to be treated as instances of the same type through a common interface.

### 4. Abstraction
Abstraction involves hiding complex implementation details while exposing only the essential features of an object.

## Benefits of OOP

- **Code Reusability**: Classes can be reused across different parts of an application
- **Modularity**: Code is organized into logical units that are easier to maintain
- **Flexibility**: Polymorphism allows for flexible and extensible code
- **Maintainability**: Encapsulation makes code easier to debug and modify

## OOP vs Procedural Programming

| Procedural | Object-Oriented |
|------------|-----------------|
| Functions are primary | Objects are primary |
| Data and functions are separate | Data and methods are bundled together |
| Top-down approach | Bottom-up approach |
| Less secure | More secure through encapsulation |

Understanding these fundamentals is crucial for effective object-oriented design and implementation.`
        },
        {
          type: 'lesson',
          title: 'Classes and Objects',
          content: `# Classes and Objects

## Understanding Classes

A class is a blueprint or template for creating objects. It defines the structure and behavior that objects of that type will have.

## Class Components

### Attributes (Properties)
- Data stored within a class
- Represent the state of an object
- Can be public, private, or protected

### Methods (Functions)
- Functions defined within a class
- Operate on the class's data
- Define the behavior of objects

## Creating Classes in JavaScript

\`\`\`javascript
class Car {
  // Constructor method
  constructor(make, model, year) {
    this.make = make;
    this.model = model;
    this.year = year;
    this.isRunning = false;
  }
  
  // Method to start the car
  start() {
    this.isRunning = true;
    console.log(\`\${this.make} \${this.model} is now running.\`);
  }
  
  // Method to stop the car
  stop() {
    this.isRunning = false;
    console.log(\`\${this.make} \${this.model} has stopped.\`);
  }
  
  // Method to get car info
  getInfo() {
    return \`\${this.year} \${this.make} \${this.model}\`;
  }
}
\`\`\`

## Creating Objects (Instances)

\`\`\`javascript
// Creating instances of the Car class
const myCar = new Car('Toyota', 'Camry', 2022);
const friendsCar = new Car('Honda', 'Civic', 2021);

// Using object methods
myCar.start(); // Toyota Camry is now running.
console.log(myCar.getInfo()); // 2022 Toyota Camry
\`\`\`

## Best Practices

1. **Use meaningful class names** (PascalCase convention)
2. **Keep classes focused** on a single responsibility
3. **Initialize all attributes** in the constructor
4. **Use descriptive method names**
5. **Document your classes** with comments`
        },
        {
          type: 'interactive_exercise',
          title: 'Create Your First Class',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Create a class called 'Student' with the following requirements:

1. Constructor that takes name, age, and major as parameters
2. Method called 'introduce()' that returns a string introduction
3. Method called 'study(subject)' that returns a studying message
4. Create an instance of the Student class and call both methods

Example output:
- introduce(): "Hi, I'm John, a 20-year-old Computer Science student."
- study('Math'): "John is studying Math"`,
            initialCode: `// Create your Student class here
class Student {
  // Add constructor and methods
}

// Create an instance and test your methods
const student = new Student('John', 20, 'Computer Science');
console.log(student.introduce());
console.log(student.study('Math'));`,
            expectedOutput: `Hi, I'm John, a 20-year-old Computer Science student.
John is studying Math`,
            hints: [
              'Remember to use the constructor method to initialize properties',
              'Use template literals (backticks) for string interpolation',
              'Methods should return strings, not just console.log them',
              'Use this.propertyName to access instance properties'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Programming Assignment',
          title: 'Class Design and Implementation',
          points: 75,
          dueWeek: 2
        }
      ]
    },
    {
      id: 'encapsulation-data-hiding',
      title: 'Encapsulation and Data Hiding',
      duration: '2 weeks',
      learningObjectives: [
        'Implement proper encapsulation techniques',
        'Understand public, private, and protected access modifiers',
        'Create getter and setter methods',
        'Apply data validation and protection strategies'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Understanding Encapsulation',
          content: `# Encapsulation and Data Hiding

## What is Encapsulation?

Encapsulation is one of the fundamental principles of object-oriented programming. It refers to the bundling of data (attributes) and methods (functions) that operate on that data within a single unit (class), while restricting direct access to some of the object's components.

## Benefits of Encapsulation

1. **Data Protection**: Prevents unauthorized access and modification
2. **Code Maintainability**: Changes to internal implementation don't affect external code
3. **Modularity**: Creates self-contained units of functionality
4. **Security**: Sensitive data can be hidden from external access

## Access Modifiers

### Public
- Accessible from anywhere
- Default in JavaScript
- No special syntax needed

### Private
- Only accessible within the same class
- Use # prefix in modern JavaScript
- Protected from external access

### Protected
- Accessible within the class and its subclasses
- Not natively supported in JavaScript but can be simulated

## Private Fields in JavaScript

\`\`\`javascript
class BankAccount {
  // Private fields (use # prefix)
  #balance;
  #accountNumber;
  
  constructor(initialBalance, accountNumber) {
    this.#balance = initialBalance;
    this.#accountNumber = accountNumber;
    this.owner = ''; // Public field
  }
  
  // Public method to access private balance
  getBalance() {
    return this.#balance;
  }
  
  // Public method to deposit money
  deposit(amount) {
    if (amount > 0) {
      this.#balance += amount;
      return true;
    }
    return false;
  }
  
  // Public method to withdraw money
  withdraw(amount) {
    if (amount > 0 && amount <= this.#balance) {
      this.#balance -= amount;
      return true;
    }
    return false;
  }
  
  // Private method for internal calculations
  #calculateInterest() {
    return this.#balance * 0.02; // 2% interest
  }
}
\`\`\`

## Getter and Setter Methods

Getters and setters provide controlled access to private data:

\`\`\`javascript
class Temperature {
  #celsius;
  
  constructor(celsius = 0) {
    this.celsius = celsius; // Uses setter
  }
  
  // Getter for celsius
  get celsius() {
    return this.#celsius;
  }
  
  // Setter for celsius with validation
  set celsius(value) {
    if (typeof value !== 'number') {
      throw new Error('Temperature must be a number');
    }
    if (value < -273.15) {
      throw new Error('Temperature cannot be below absolute zero');
    }
    this.#celsius = value;
  }
  
  // Getter for fahrenheit
  get fahrenheit() {
    return (this.#celsius * 9/5) + 32;
  }
  
  // Setter for fahrenheit
  set fahrenheit(value) {
    this.celsius = (value - 32) * 5/9; // Uses celsius setter
  }
}
\`\`\`

## Best Practices

1. **Make data private by default** and expose only what's necessary
2. **Use getters and setters** for controlled access
3. **Validate data** in setter methods
4. **Keep internal methods private**
5. **Document public interfaces** clearly`
        },
        {
          type: 'interactive_exercise',
          title: 'Implement Encapsulation',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Create a class called 'Employee' with proper encapsulation:

1. Private fields: #name, #salary, #id
2. Constructor that takes name, salary, and id
3. Getter methods for name and id (salary should remain private)
4. Setter for salary with validation (must be positive number)
5. Method 'giveRaise(percentage)' that increases salary
6. Method 'getAnnualSalary()' that returns yearly salary

Test your implementation by creating an employee and testing all methods.`,
            initialCode: `// Create your Employee class with proper encapsulation
class Employee {
  // Add private fields and methods here
}

// Test your implementation
const emp = new Employee('Alice Johnson', 5000, 'EMP001');
console.log('Name:', emp.name);
console.log('ID:', emp.id);
console.log('Annual Salary:', emp.getAnnualSalary());

emp.giveRaise(10); // 10% raise
console.log('After raise:', emp.getAnnualSalary());`,
            expectedOutput: `Name: Alice Johnson
ID: EMP001
Annual Salary: 60000
After raise: 66000`,
            hints: [
              'Use # prefix for private fields in JavaScript',
              'Implement getter methods using the get keyword',
              'Validate salary input to ensure it\'s a positive number',
              'Annual salary = monthly salary × 12',
              'Percentage raise: new_salary = old_salary × (1 + percentage/100)'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Encapsulation Project',
          title: 'Secure Banking System Implementation',
          points: 100,
          dueWeek: 4
        }
      ]
    },
    {
      id: 'inheritance-polymorphism',
      title: 'Inheritance and Polymorphism',
      duration: '3 weeks',
      learningObjectives: [
        'Implement inheritance hierarchies using extends keyword',
        'Understand method overriding and super keyword usage',
        'Apply polymorphism in practical scenarios',
        'Design effective class hierarchies'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Inheritance Fundamentals',
          content: `# Inheritance in Object-Oriented Programming

## What is Inheritance?

Inheritance is a mechanism that allows a class to inherit properties and methods from another class. The class that inherits is called a subclass (or derived class), and the class being inherited from is called a superclass (or base class).

## Benefits of Inheritance

1. **Code Reusability**: Avoid duplicating code across similar classes
2. **Hierarchical Organization**: Create logical relationships between classes
3. **Polymorphism**: Enable objects to be treated as instances of their parent class
4. **Maintainability**: Changes to the parent class automatically propagate to children

## Inheritance Syntax in JavaScript

\`\`\`javascript
// Parent class (Superclass)
class Animal {
  constructor(name, species) {
    this.name = name;
    this.species = species;
  }
  
  makeSound() {
    console.log(\`\${this.name} makes a sound\`);
  }
  
  sleep() {
    console.log(\`\${this.name} is sleeping\`);
  }
}

// Child class (Subclass)
class Dog extends Animal {
  constructor(name, breed) {
    super(name, 'Canine'); // Call parent constructor
    this.breed = breed;
  }
  
  // Override parent method
  makeSound() {
    console.log(\`\${this.name} barks: Woof! Woof!\`);
  }
  
  // New method specific to Dog
  fetch() {
    console.log(\`\${this.name} is fetching the ball\`);
  }
}
\`\`\`

## The super Keyword

The \`super\` keyword is used to:
1. **Call the parent constructor**: \`super(arguments)\`
2. **Access parent methods**: \`super.methodName()\`

\`\`\`javascript
class Cat extends Animal {
  constructor(name, breed, isIndoor) {
    super(name, 'Feline'); // Call Animal constructor
    this.breed = breed;
    this.isIndoor = isIndoor;
  }
  
  makeSound() {
    super.sleep(); // Call parent method
    console.log(\`\${this.name} meows: Meow!\`);
  }
}
\`\`\`

## Method Overriding

Child classes can override parent methods to provide specialized behavior:

\`\`\`javascript
class Bird extends Animal {
  constructor(name, wingspan) {
    super(name, 'Avian');
    this.wingspan = wingspan;
  }
  
  // Override makeSound method
  makeSound() {
    console.log(\`\${this.name} chirps and sings\`);
  }
  
  // Override sleep method
  sleep() {
    console.log(\`\${this.name} sleeps in a nest\`);
  }
  
  // New method
  fly() {
    console.log(\`\${this.name} flies with a \${this.wingspan} inch wingspan\`);
  }
}
\`\`\`

## Inheritance Hierarchies

You can create multi-level inheritance:

\`\`\`javascript
class Vehicle {
  constructor(make, model) {
    this.make = make;
    this.model = model;
  }
  
  start() {
    console.log('Vehicle started');
  }
}

class Car extends Vehicle {
  constructor(make, model, doors) {
    super(make, model);
    this.doors = doors;
  }
  
  drive() {
    console.log('Car is driving');
  }
}

class ElectricCar extends Car {
  constructor(make, model, doors, batteryCapacity) {
    super(make, model, doors);
    this.batteryCapacity = batteryCapacity;
  }
  
  charge() {
    console.log('Electric car is charging');
  }
}
\`\`\`

## Best Practices

1. **Use inheritance for "is-a" relationships**
2. **Keep inheritance hierarchies shallow** (generally 3-4 levels max)
3. **Override methods thoughtfully**
4. **Always call super() in constructors** when extending classes
5. **Consider composition** as an alternative to deep inheritance`
        },
        {
          type: 'interactive_exercise',
          title: 'Build an Inheritance Hierarchy',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Create an inheritance hierarchy for shapes:

1. Base class 'Shape' with:
   - Constructor taking color
   - Method 'getInfo()' returning color information
   - Abstract method 'getArea()' (just return 0 for now)

2. Child class 'Rectangle' extending Shape with:
   - Constructor taking color, width, height
   - Override 'getArea()' to calculate rectangle area
   - Method 'getPerimeter()' to calculate perimeter

3. Child class 'Square' extending Rectangle with:
   - Constructor taking color and side length
   - Should use Rectangle's constructor appropriately

Test your hierarchy by creating instances and calling methods.`,
            initialCode: `// Create your Shape hierarchy here
class Shape {
  // Implement base class
}

class Rectangle extends Shape {
  // Implement Rectangle class
}

class Square extends Rectangle {
  // Implement Square class
}

// Test your implementation
const rect = new Rectangle('blue', 5, 3);
const square = new Square('red', 4);

console.log(rect.getInfo());
console.log('Rectangle area:', rect.getArea());
console.log('Rectangle perimeter:', rect.getPerimeter());

console.log(square.getInfo());
console.log('Square area:', square.getArea());
console.log('Square perimeter:', square.getPerimeter());`,
            expectedOutput: `This shape is blue
Rectangle area: 15
Rectangle perimeter: 16
This shape is red
Square area: 16
Square perimeter: 16`,
            hints: [
              'Use super() to call parent constructors',
              'Rectangle area = width × height',
              'Rectangle perimeter = 2 × (width + height)',
              'Square is a special case of Rectangle where width = height',
              'Override methods to provide specific implementations'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Inheritance Design Project',
          title: 'Polymorphic Animal Kingdom Simulation',
          points: 125,
          dueWeek: 7
        }
      ]
    },
    {
      id: 'design-patterns',
      title: 'Design Patterns',
      duration: '3 weeks',
      learningObjectives: [
        'Understand common design patterns and their applications',
        'Implement Singleton, Factory, and Observer patterns',
        'Apply design patterns to solve real-world problems',
        'Recognize when and how to use different patterns'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Introduction to Design Patterns',
          content: `# Design Patterns in Object-Oriented Programming

## What are Design Patterns?

Design patterns are reusable solutions to commonly occurring problems in software design. They represent best practices and provide a common vocabulary for developers to communicate about design solutions.

## Benefits of Design Patterns

1. **Reusability**: Proven solutions that can be applied across projects
2. **Communication**: Common vocabulary for design discussions
3. **Best Practices**: Incorporate years of software engineering experience
4. **Flexibility**: Make code more maintainable and extensible

## Categories of Design Patterns

### 1. Creational Patterns
- Control object creation mechanisms
- Examples: Singleton, Factory, Builder

### 2. Structural Patterns
- Deal with object composition and relationships
- Examples: Adapter, Decorator, Composite

### 3. Behavioral Patterns
- Focus on communication and interaction between objects
- Examples: Observer, Strategy, Command

## The Singleton Pattern

Ensures a class has only one instance and provides global access to it.

\`\`\`javascript
class DatabaseConnection {
  static instance = null;
  
  constructor() {
    if (DatabaseConnection.instance) {
      return DatabaseConnection.instance;
    }
    
    this.connection = null;
    this.isConnected = false;
    DatabaseConnection.instance = this;
  }
  
  connect() {
    if (!this.isConnected) {
      this.connection = 'Connected to database';
      this.isConnected = true;
      console.log('Database connected');
    }
  }
  
  disconnect() {
    if (this.isConnected) {
      this.connection = null;
      this.isConnected = false;
      console.log('Database disconnected');
    }
  }
  
  getStatus() {
    return this.isConnected ? 'Connected' : 'Disconnected';
  }
}

// Usage
const db1 = new DatabaseConnection();
const db2 = new DatabaseConnection();
console.log(db1 === db2); // true - same instance
\`\`\`

## The Factory Pattern

Creates objects without specifying their exact classes.

\`\`\`javascript
class Vehicle {
  constructor(type, make, model) {
    this.type = type;
    this.make = make;
    this.model = model;
  }
  
  getInfo() {
    return \`\${this.type}: \${this.make} \${this.model}\`;
  }
}

class Car extends Vehicle {
  constructor(make, model) {
    super('Car', make, model);
    this.doors = 4;
  }
}

class Motorcycle extends Vehicle {
  constructor(make, model) {
    super('Motorcycle', make, model);
    this.wheels = 2;
  }
}

class Truck extends Vehicle {
  constructor(make, model) {
    super('Truck', make, model);
    this.capacity = '2 tons';
  }
}

class VehicleFactory {
  static createVehicle(type, make, model) {
    switch(type.toLowerCase()) {
      case 'car':
        return new Car(make, model);
      case 'motorcycle':
        return new Motorcycle(make, model);
      case 'truck':
        return new Truck(make, model);
      default:
        throw new Error('Unknown vehicle type');
    }
  }
}

// Usage
const car = VehicleFactory.createVehicle('car', 'Toyota', 'Camry');
const bike = VehicleFactory.createVehicle('motorcycle', 'Honda', 'CBR');
\`\`\`

## The Observer Pattern

Defines a dependency between objects so that when one object changes state, all dependents are notified.

\`\`\`javascript
class Observable {
  constructor() {
    this.observers = [];
  }
  
  subscribe(observer) {
    this.observers.push(observer);
  }
  
  unsubscribe(observer) {
    this.observers = this.observers.filter(obs => obs !== observer);
  }
  
  notify(data) {
    this.observers.forEach(observer => observer.update(data));
  }
}

class NewsAgency extends Observable {
  constructor() {
    super();
    this.news = '';
  }
  
  setNews(news) {
    this.news = news;
    this.notify(news);
  }
}

class NewsChannel {
  constructor(name) {
    this.name = name;
  }
  
  update(news) {
    console.log(\`\${this.name} received news: \${news}\`);
  }
}

// Usage
const agency = new NewsAgency();
const cnn = new NewsChannel('CNN');
const bbc = new NewsChannel('BBC');

agency.subscribe(cnn);
agency.subscribe(bbc);
agency.setNews('Breaking: New design pattern discovered!');
\`\`\`

## When to Use Design Patterns

- **Don't force patterns** where they're not needed
- **Understand the problem** before applying a pattern
- **Consider simpler solutions** first
- **Patterns should improve** code quality, not complicate it`
        },
        {
          type: 'interactive_exercise',
          title: 'Implement the Strategy Pattern',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Implement the Strategy pattern for a payment system:

1. Create a 'PaymentStrategy' base class with a 'pay(amount)' method
2. Create specific strategy classes:
   - 'CreditCardPayment' that logs "Paid $X with credit card"
   - 'PayPalPayment' that logs "Paid $X with PayPal"
   - 'BankTransferPayment' that logs "Paid $X with bank transfer"
3. Create a 'PaymentProcessor' class that:
   - Takes a payment strategy in constructor
   - Has 'setPaymentStrategy(strategy)' method
   - Has 'processPayment(amount)' method that uses current strategy
4. Test by processing payments with different strategies

The Strategy pattern allows selecting algorithms at runtime.`,
            initialCode: `// Implement the Strategy pattern for payments
class PaymentStrategy {
  // Base strategy class
}

class CreditCardPayment extends PaymentStrategy {
  // Implement credit card payment
}

class PayPalPayment extends PaymentStrategy {
  // Implement PayPal payment
}

class BankTransferPayment extends PaymentStrategy {
  // Implement bank transfer payment
}

class PaymentProcessor {
  // Payment context class
}

// Test your implementation
const processor = new PaymentProcessor(new CreditCardPayment());
processor.processPayment(100);

processor.setPaymentStrategy(new PayPalPayment());
processor.processPayment(250);

processor.setPaymentStrategy(new BankTransferPayment());
processor.processPayment(500);`,
            expectedOutput: `Paid $100 with credit card
Paid $250 with PayPal
Paid $500 with bank transfer`,
            hints: [
              'The base PaymentStrategy class can be abstract with a pay() method',
              'Each concrete strategy implements pay() differently',
              'PaymentProcessor stores and uses the current strategy',
              'Use composition over inheritance - processor HAS-A strategy',
              'The setPaymentStrategy method allows changing strategies at runtime'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Design Patterns Implementation',
          title: 'Multi-Pattern Software Architecture',
          points: 150,
          dueWeek: 10
        }
      ]
    },
    {
      id: 'advanced-oop-concepts',
      title: 'Advanced OOP Concepts',
      duration: '3 weeks',
      learningObjectives: [
        'Implement abstract classes and interfaces',
        'Understand composition vs inheritance trade-offs',
        'Apply SOLID principles in object-oriented design',
        'Design complex systems using advanced OOP techniques'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Abstract Classes and Interfaces',
          content: `# Abstract Classes and Interfaces

## Abstract Classes

Abstract classes are classes that cannot be instantiated directly and are designed to be extended by other classes. They often contain abstract methods that must be implemented by subclasses.

\`\`\`javascript
// Abstract class simulation in JavaScript
class AbstractShape {
  constructor(color) {
    if (new.target === AbstractShape) {
      throw new Error('Cannot instantiate abstract class');
    }
    this.color = color;
  }
  
  // Abstract method (must be overridden)
  getArea() {
    throw new Error('Abstract method must be implemented');
  }
  
  // Concrete method (can be used as-is)
  getColor() {
    return this.color;
  }
  
  // Template method pattern
  displayInfo() {
    console.log(\`Color: \${this.getColor()}, Area: \${this.getArea()}\`);
  }
}

class Circle extends AbstractShape {
  constructor(color, radius) {
    super(color);
    this.radius = radius;
  }
  
  getArea() {
    return Math.PI * this.radius ** 2;
  }
}
\`\`\`

## Interface Simulation in JavaScript

JavaScript doesn't have built-in interfaces, but we can simulate them:

\`\`\`javascript
// Interface definition (documentation purposes)
class Drawable {
  draw() {
    throw new Error('draw() method must be implemented');
  }
}

class Resizable {
  resize(factor) {
    throw new Error('resize() method must be implemented');
  }
}

// Multiple interface implementation
class Rectangle extends AbstractShape {
  constructor(color, width, height) {
    super(color);
    this.width = width;
    this.height = height;
  }
  
  getArea() {
    return this.width * this.height;
  }
  
  // Implement Drawable interface
  draw() {
    console.log(\`Drawing a \${this.color} rectangle\`);
  }
  
  // Implement Resizable interface
  resize(factor) {
    this.width *= factor;
    this.height *= factor;
  }
}
\`\`\`

## Composition vs Inheritance

### Inheritance ("is-a" relationship)
- Creates tight coupling between classes
- Changes to parent class affect all children
- Can lead to deep hierarchies

### Composition ("has-a" relationship)
- More flexible and maintainable
- Easier to test and mock
- Follows "favor composition over inheritance" principle

\`\`\`javascript
// Inheritance approach
class Bird {
  fly() { console.log('Flying'); }
  eat() { console.log('Eating'); }
}

class Penguin extends Bird {
  fly() { throw new Error('Penguins cannot fly'); } // Problem!
}

// Composition approach
class FlyingBehavior {
  fly() { console.log('Flying high'); }
}

class WalkingBehavior {
  walk() { console.log('Walking'); }
}

class SwimmingBehavior {
  swim() { console.log('Swimming'); }
}

class Bird {
  constructor(behaviors = {}) {
    this.flyingBehavior = behaviors.flying;
    this.walkingBehavior = behaviors.walking;
    this.swimmingBehavior = behaviors.swimming;
  }
  
  performFly() {
    if (this.flyingBehavior) {
      this.flyingBehavior.fly();
    } else {
      console.log('Cannot fly');
    }
  }
  
  performWalk() {
    if (this.walkingBehavior) {
      this.walkingBehavior.walk();
    }
  }
  
  performSwim() {
    if (this.swimmingBehavior) {
      this.swimmingBehavior.swim();
    }
  }
}

// Usage
const eagle = new Bird({
  flying: new FlyingBehavior(),
  walking: new WalkingBehavior()
});

const penguin = new Bird({
  walking: new WalkingBehavior(),
  swimming: new SwimmingBehavior()
});
\`\`\`

## SOLID Principles

### Single Responsibility Principle (SRP)
A class should have only one reason to change.

\`\`\`javascript
// Bad: Multiple responsibilities
class User {
  constructor(name, email) {
    this.name = name;
    this.email = email;
  }
  
  save() {
    // Database logic
  }
  
  sendEmail() {
    // Email logic
  }
  
  validateData() {
    // Validation logic
  }
}

// Good: Single responsibility
class User {
  constructor(name, email) {
    this.name = name;
    this.email = email;
  }
}

class UserRepository {
  save(user) {
    // Database logic
  }
}

class EmailService {
  sendEmail(user, message) {
    // Email logic
  }
}

class UserValidator {
  validate(user) {
    // Validation logic
  }
}
\`\`\`

### Open/Closed Principle (OCP)
Classes should be open for extension but closed for modification.

\`\`\`javascript
// Extension through inheritance/composition
class AreaCalculator {
  calculate(shapes) {
    return shapes.reduce((total, shape) => total + shape.getArea(), 0);
  }
}

// Adding new shapes doesn't require modifying AreaCalculator
class Triangle extends AbstractShape {
  constructor(color, base, height) {
    super(color);
    this.base = base;
    this.height = height;
  }
  
  getArea() {
    return 0.5 * this.base * this.height;
  }
}
\`\`\`

## Best Practices

1. **Prefer composition over inheritance**
2. **Use abstract classes for shared behavior**
3. **Define clear interfaces for contracts**
4. **Follow SOLID principles**
5. **Keep classes focused and cohesive**`
        },
        {
          type: 'interactive_exercise',
          title: 'Apply SOLID Principles',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Refactor this code to follow SOLID principles:

The given code violates several SOLID principles. Your task is to:

1. Separate concerns (SRP) - split the Order class responsibilities
2. Make it extensible (OCP) - allow adding new discount types without modification
3. Use composition for discount calculations

Original problematic code structure:
- Order class handles order data, discount calculation, and saving
- Adding new discount types requires modifying existing code

Create these classes:
- Order: holds order data only
- DiscountCalculator: abstract base for discount strategies
- PercentageDiscount: specific discount implementation
- OrderRepository: handles saving orders
- OrderService: coordinates the operations

Test with an order containing items and applying a 10% discount.`,
            initialCode: `// Refactor this code to follow SOLID principles

// Current problematic implementation (don't modify, use as reference):
/*
class Order {
  constructor() {
    this.items = [];
    this.total = 0;
  }
  
  addItem(item) {
    this.items.push(item);
    this.total += item.price;
  }
  
  // Violates SRP - multiple responsibilities
  calculateDiscount(type, value) {
    if (type === 'percentage') {
      return this.total * (value / 100);
    }
    // Would need to modify this method for new discount types (violates OCP)
  }
  
  save() {
    // Database logic mixed with order logic
  }
}
*/

// Create your SOLID-compliant implementation here:
class Order {
  // Only order data and basic operations
}

class DiscountCalculator {
  // Abstract discount calculator
}

class PercentageDiscount extends DiscountCalculator {
  // Specific discount implementation
}

class OrderRepository {
  // Handle order persistence
}

class OrderService {
  // Coordinate order operations
}

// Test your implementation
const order = new Order();
order.addItem({ name: 'Laptop', price: 1000 });
order.addItem({ name: 'Mouse', price: 50 });

const discount = new PercentageDiscount(10); // 10%
const repository = new OrderRepository();
const orderService = new OrderService(repository);

const finalTotal = orderService.processOrder(order, discount);
console.log('Original total:', order.getTotal());
console.log('Discount amount:', discount.calculate(order.getTotal()));
console.log('Final total:', finalTotal);`,
            expectedOutput: `Original total: 1050
Discount amount: 105
Final total: 945`,
            hints: [
              'Order class should only manage items and calculate totals',
              'Use the Strategy pattern for discount calculations',
              'OrderRepository handles all database operations',
              'OrderService coordinates between Order, Discount, and Repository',
              'Each class should have a single, well-defined responsibility'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'SOLID Principles Refactoring Project',
          title: 'Legacy Code Modernization Using SOLID Principles',
          points: 175,
          dueWeek: 13
        }
      ]
    },
    {
      id: 'testing-debugging-oop',
      title: 'Testing and Debugging OOP Code',
      duration: '2 weeks',
      learningObjectives: [
        'Write effective unit tests for object-oriented code',
        'Apply debugging techniques specific to OOP',
        'Understand mocking and dependency injection',
        'Implement test-driven development practices'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Testing Object-Oriented Code',
          content: `# Testing Object-Oriented Code

## Why Test OOP Code?

Object-oriented code introduces complexity through inheritance, polymorphism, and object interactions. Proper testing ensures:

1. **Individual classes work correctly** (unit testing)
2. **Objects interact properly** (integration testing)
3. **Inheritance hierarchies behave as expected**
4. **Polymorphic behavior is consistent**

## Unit Testing Basics

\`\`\`javascript
// Example class to test
class Calculator {
  add(a, b) {
    return a + b;
  }
  
  divide(a, b) {
    if (b === 0) {
      throw new Error('Division by zero');
    }
    return a / b;
  }
}

// Simple test framework simulation
class TestRunner {
  static test(description, testFunction) {
    try {
      testFunction();
      console.log(\`✓ \${description}\`);
    } catch (error) {
      console.log(\`✗ \${description}: \${error.message}\`);
    }
  }
  
  static assertEqual(actual, expected, message = '') {
    if (actual !== expected) {
      throw new Error(\`Expected \${expected}, got \${actual}. \${message}\`);
    }
  }
  
  static assertThrows(fn, expectedError) {
    try {
      fn();
      throw new Error('Expected function to throw an error');
    } catch (error) {
      if (error.message !== expectedError) {
        throw new Error(\`Expected error "\${expectedError}", got "\${error.message}"\`);
      }
    }
  }
}

// Test cases
TestRunner.test('Calculator adds numbers correctly', () => {
  const calc = new Calculator();
  TestRunner.assertEqual(calc.add(2, 3), 5);
  TestRunner.assertEqual(calc.add(-1, 1), 0);
});

TestRunner.test('Calculator handles division by zero', () => {
  const calc = new Calculator();
  TestRunner.assertThrows(
    () => calc.divide(10, 0),
    'Division by zero'
  );
});
\`\`\`

## Testing Inheritance

\`\`\`javascript
class Animal {
  constructor(name) {
    this.name = name;
  }
  
  makeSound() {
    return 'Some sound';
  }
}

class Dog extends Animal {
  makeSound() {
    return 'Woof!';
  }
  
  fetch() {
    return \`\${this.name} is fetching\`;
  }
}

// Testing inheritance
TestRunner.test('Dog inherits from Animal', () => {
  const dog = new Dog('Buddy');
  TestRunner.assertEqual(dog.name, 'Buddy');
  TestRunner.assertEqual(dog instanceof Animal, true);
  TestRunner.assertEqual(dog instanceof Dog, true);
});

TestRunner.test('Dog overrides makeSound method', () => {
  const dog = new Dog('Buddy');
  TestRunner.assertEqual(dog.makeSound(), 'Woof!');
});
\`\`\`

## Mocking and Dependency Injection

\`\`\`javascript
// Class with external dependencies
class UserService {
  constructor(database, emailService) {
    this.database = database;
    this.emailService = emailService;
  }
  
  createUser(userData) {
    const user = this.database.save(userData);
    this.emailService.sendWelcomeEmail(user.email);
    return user;
  }
}

// Mock implementations for testing
class MockDatabase {
  save(userData) {
    return { id: 1, ...userData };
  }
}

class MockEmailService {
  constructor() {
    this.sentEmails = [];
  }
  
  sendWelcomeEmail(email) {
    this.sentEmails.push(email);
  }
}

// Testing with mocks
TestRunner.test('UserService creates user and sends email', () => {
  const mockDb = new MockDatabase();
  const mockEmail = new MockEmailService();
  const userService = new UserService(mockDb, mockEmail);
  
  const user = userService.createUser({ name: 'John', email: 'john@test.com' });
  
  TestRunner.assertEqual(user.name, 'John');
  TestRunner.assertEqual(mockEmail.sentEmails.length, 1);
  TestRunner.assertEqual(mockEmail.sentEmails[0], 'john@test.com');
});
\`\`\`

## Test-Driven Development (TDD)

TDD follows the Red-Green-Refactor cycle:

1. **Red**: Write a failing test
2. **Green**: Write minimal code to pass the test
3. **Refactor**: Improve the code while keeping tests passing

\`\`\`javascript
// 1. Red: Write failing test first
TestRunner.test('BankAccount should have initial balance', () => {
  const account = new BankAccount(100);
  TestRunner.assertEqual(account.getBalance(), 100);
});

// 2. Green: Implement minimal code
class BankAccount {
  constructor(initialBalance) {
    this.balance = initialBalance;
  }
  
  getBalance() {
    return this.balance;
  }
}

// 3. Add more tests and refactor
TestRunner.test('BankAccount should allow deposits', () => {
  const account = new BankAccount(100);
  account.deposit(50);
  TestRunner.assertEqual(account.getBalance(), 150);
});
\`\`\`

## Debugging OOP Code

### Common OOP Debugging Techniques

1. **Check object state**: Use console.log or debugger to inspect object properties
2. **Verify method calls**: Ensure methods are being called on correct objects
3. **Test inheritance chains**: Verify super() calls and method overrides
4. **Check for null/undefined**: Objects might not be properly initialized

\`\`\`javascript
class DebuggableClass {
  constructor(value) {
    this.value = value;
    console.log('Object created with value:', this.value);
  }
  
  processValue() {
    console.log('Processing value:', this.value);
    
    if (this.value === undefined) {
      console.error('Value is undefined!');
      return;
    }
    
    return this.value * 2;
  }
}
\`\`\`

## Best Testing Practices

1. **Test one thing at a time**: Each test should verify a single behavior
2. **Use descriptive test names**: Make it clear what's being tested
3. **Follow AAA pattern**: Arrange, Act, Assert
4. **Test edge cases**: Null values, empty arrays, boundary conditions
5. **Mock external dependencies**: Isolate the code being tested
6. **Keep tests independent**: Tests shouldn't depend on each other`
        },
        {
          type: 'interactive_exercise',
          title: 'Write Tests for OOP Code',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Create a comprehensive test suite for the following ShoppingCart class:

The ShoppingCart class should:
1. Start with an empty cart
2. Allow adding items with name and price
3. Calculate total price
4. Apply discounts
5. Clear the cart
6. Throw error when adding items with invalid price

Your task:
1. Implement the ShoppingCart class
2. Create a simple test framework with assertEqual and assertThrows methods
3. Write at least 6 test cases covering different scenarios
4. Include edge cases and error conditions

Test scenarios to include:
- Empty cart has zero total
- Adding items increases total
- Applying discount reduces total
- Clearing cart resets total
- Invalid price throws error
- Multiple items calculate correctly`,
            initialCode: `// Implement ShoppingCart class and test framework

class ShoppingCart {
  // Implement shopping cart functionality
}

class TestFramework {
  static assertEqual(actual, expected, message = '') {
    // Implement assertion method
  }
  
  static assertThrows(fn, expectedErrorMessage = '') {
    // Implement error testing method
  }
  
  static test(description, testFunction) {
    // Implement test runner
  }
}

// Write your test cases here
TestFramework.test('Empty cart has zero total', () => {
  // Your test implementation
});

TestFramework.test('Adding items increases total', () => {
  // Your test implementation
});

TestFramework.test('Applying discount reduces total', () => {
  // Your test implementation
});

TestFramework.test('Clearing cart resets total', () => {
  // Your test implementation
});

TestFramework.test('Invalid price throws error', () => {
  // Your test implementation
});

TestFramework.test('Multiple items calculate correctly', () => {
  // Your test implementation
});`,
            expectedOutput: `✓ Empty cart has zero total
✓ Adding items increases total  
✓ Applying discount reduces total
✓ Clearing cart resets total
✓ Invalid price throws error
✓ Multiple items calculate correctly`,
            hints: [
              'ShoppingCart needs properties for items and discount',
              'addItem should validate price (must be positive number)',
              'applyDiscount should be a percentage (0-100)',
              'assertEqual should throw an error if values don\'t match',
              'assertThrows should catch and verify the expected error',
              'Test runner should catch exceptions and report success/failure'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Unit Testing Project',
          title: 'OOP Code Testing Framework Implementation',
          points: 100,
          dueWeek: 14
        }
      ]
    }
  ],
  assessments: [
    {
      id: 'midterm',
      title: 'Midterm Examination',
      type: 'exam',
      date: 'Week 8',
      duration: '120 minutes',
      points: 200,
      description: 'Comprehensive exam covering OOP fundamentals, encapsulation, inheritance, and basic design patterns.'
    },
    {
      id: 'final',
      title: 'Final Examination',
      type: 'exam',
      date: 'Week 15',
      duration: '180 minutes',
      points: 250,
      description: 'Cumulative exam covering all course material including advanced OOP concepts, design patterns, and SOLID principles.'
    },
    {
      id: 'final-project',
      title: 'Object-Oriented Software System',
      type: 'project',
      date: 'Week 15',
      points: 300,
      description: 'Design and implement a complete object-oriented software system demonstrating inheritance hierarchies, design patterns, and proper testing practices.'
    }
  ],
  resources: [
    {
      category: 'Textbooks',
      items: [
        'Head First Object-Oriented Analysis and Design by Brett McLaughlin',
        'Clean Code: A Handbook of Agile Software Craftsmanship by Robert Martin',
        'Design Patterns: Elements of Reusable Object-Oriented Software by Gang of Four',
        'Effective Java by Joshua Bloch (for Java concepts)'
      ]
    },
    {
      category: 'Online Resources',
      items: [
        'MDN JavaScript Classes Documentation',
        'JavaScript.info Object-oriented Programming Section',
        'Refactoring Guru Design Patterns',
        'SOLID Principles Explained',
        'TypeScript Handbook for Advanced OOP Features'
      ]
    },
    {
      category: 'Development Tools',
      items: [
        'Visual Studio Code with OOP Extensions',
        'UML Diagramming Tools (Lucidchart, Draw.io)',
        'Jest Testing Framework',
        'ESLint for Code Quality',
        'Git for Version Control'
      ]
    },
    {
      category: 'Practice Platforms',
      items: [
        'LeetCode Object-Oriented Design Problems',
        'System Design Primer on GitHub',
        'Codewars OOP Kata Challenges',
        'Exercism Object-Oriented Programming Track'
      ]
    },
    {
      category: 'Reference Materials',
      items: [
        'OOP Design Patterns Quick Reference',
        'SOLID Principles Cheat Sheet',
        'UML Class Diagram Notation Guide',
        'JavaScript OOP Best Practices',
        'Testing Strategies for OOP Code'
      ]
    }
  ],
  gradingScale: {
    'A': '93-100%',
    'A-': '90-92%',
    'B+': '87-89%',
    'B': '83-86%',
    'B-': '80-82%',
    'C+': '77-79%',
    'C': '73-76%',
    'C-': '70-72%',
    'D': '60-69%',
    'F': 'Below 60%'
  },
  gradingBreakdown: {
    'Module Assessments': '25%',
    'Midterm Exam': '20%',
    'Final Project': '30%',
    'Final Exam': '25%'
  },
  attendancePolicy: 'Regular attendance is crucial for understanding complex OOP concepts. Students are expected to participate actively in code reviews, design discussions, and collaborative programming exercises.',
  latePolicy: 'Programming assignments submitted late will be penalized 15% per day due to the iterative nature of OOP development. Extensions may be granted for significant technical challenges with proper documentation.',
  accommodations: 'Students with disabilities requiring accommodations should contact the Office of Disability Services. Accommodations may include extended time for design exercises, alternative assessment formats, and accessible development environments.',
  academicIntegrity: 'All code and designs must be original work. While discussing design principles and patterns is encouraged, copying implementations without proper attribution constitutes plagiarism. Proper citation of design pattern sources is required.',
  technologyRequirements: 'Students need a computer capable of running modern development environments, UML modeling tools, and testing frameworks. Reliable internet connection required for collaborative coding and code repositories.',
  softwareRequirements: [
    'Modern code editor (VS Code, IntelliJ, or equivalent)',
    'Node.js runtime environment',
    'Git version control system',
    'UML diagramming software',
    'Testing framework (Jest or similar)',
    'Web browser for testing and documentation'
  ],
  courseEvaluation: 'Student feedback collected through peer code reviews, design critique sessions, and comprehensive course evaluations to ensure practical relevance and industry alignment of OOP skills.'
};